import { useCallback, useEffect, useRef, useState } from 'react';
import { callFirebaseFunction } from '../../services/firebaseFunctions';
import { useAIObservability } from '../../services/productAnalytics/aiObservabilityEvents';
import { messageIsWriteAction } from './ChatMessage';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: Date;
  /** Agent-suggested quick-reply labels (e.g. ["Build it","Adjust"]) for the
   *  latest assistant turn. Live-turn only — the backend doesn't persist them,
   *  so reloaded conversations won't carry them. */
  quickReplies?: string[];
}

interface ConversationSummary {
  id: string;
  title: string | null;
  messageCount: number;
  lastMessageAt: string;
  createdAt: string;
  /** Experience template this conversation is scoped to. `null` = a global chat. */
  templateId?: string | null;
}

interface ToolAction {
  tool: string;
  result: Record<string, unknown>;
}

/** Per-tool-call timing + outcome metric. Phase-2 Mixpanel observability.
 * Mirrors `ToolCallMetric` in functions/ai/src/chatService.ts. */
interface ToolCallMetric {
  toolName: string;
  durationMs: number;
  success: boolean;
  errorClass?: string;
}

interface AIChatResponse {
  conversation_id: string;
  /** The conversation's scope (null = global). After a build re-home this is the
   *  newly-built template id, so we can retarget without a refetch. */
  template_id?: string | null;
  content: string;
  actions?: ToolAction[];
  usage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
  /** Phase-2 observability: per-tool-call timing + outcome.
   * Optional — server may omit on older deploys. */
  toolMetrics?: ToolCallMetric[];
  /** Phase-2 observability: cumulative LLM call time for the turn.
   * Optional — server may omit on older deploys. */
  modelLatencyMs?: number;
  /** Agent-suggested quick-reply labels for this turn (e.g. Build it / Adjust).
   * Present only when the agent offered them; older deploys omit it. */
  quickReplies?: string[];
}

interface UseCopilotChatOptions {
  organizationId: string;
  pageContext?: { templateId?: string } | null;
  onActions?: (actions: ToolAction[]) => void;
}

export type { ToolAction };

export function useCopilotChat({ organizationId, pageContext, onActions }: UseCopilotChatOptions) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  // The template scope of the currently-loaded conversation (null = global).
  // Kept in sync on load/send so the auto-switch effect can "keep if already
  // matching" without depending on the (possibly stale) conversation list —
  // this is what preserves a just-re-homed build conversation after navigation.
  const activeTemplateIdRef = useRef<string | null>(null);
  // The scope the auto-switch effect last resolved the active conversation for.
  // `undefined` until the first resolve; prevents re-resolving (and clobbering a
  // manual selection) on every conversation-list refresh.
  const resolvedScopeRef = useRef<string | null | undefined>(undefined);
  // Single source of truth for "a write action has happened in THIS
  // conversation" — drives suppression of the Build it/Adjust chips.
  // Set on live builds (data.actions) AND on reload (scan loaded tool
  // results), so it survives ai_loadConversation rehydrate.
  const [hasWriteActionOccurred, setHasWriteActionOccurred] = useState(false);
  const aiObs = useAIObservability();
  // Lifetime counters for session-ended summary metrics.
  const sessionStatsRef = useRef({
    totalTurns: 0,
    totalToolCalls: 0,
    totalTokens: 0,
  });

  // Conversation history
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  // Flips true once the initial list load settles (success OR failure), so the
  // scope effect knows it may resolve the active conversation — even for an
  // empty list (blank chat), where `conversations` wouldn't change.
  const [isListLoaded, setIsListLoaded] = useState(false);

  // Fetch conversation list
  const loadConversationList = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const data = await callFirebaseFunction<{ conversations: ConversationSummary[] }>(
        'ai_listConversations',
        { organization_id: organizationId }
      );
      setConversations(data.conversations);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [organizationId]);

  // Load a specific conversation
  const loadConversation = useCallback(async (conversationId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await callFirebaseFunction<{
        conversation: ConversationSummary;
        messages: Array<{
          id: string;
          role: string;
          content: string;
          createdAt: string;
          quickReplies?: string[];
        }>;
      }>('ai_loadConversation', {
        conversation_id: conversationId,
        organization_id: organizationId,
      });

      conversationIdRef.current = conversationId;
      activeTemplateIdRef.current = data.conversation.templateId ?? null;
      // Detect a prior build from the RAW messages (incl. role='tool'
      // results whose JSON content is a write-action). Must run BEFORE
      // the display filter drops tool/empty rows — otherwise the
      // Build it/Adjust chips would wrongly reappear on a reloaded
      // already-built conversation.
      setHasWriteActionOccurred(
        data.messages.some((m) => messageIsWriteAction(m.content))
      );
      setMessages(
        data.messages
          // Display only real chat turns: user messages + assistant
          // messages that have actual text. The agent's intermediate
          // tool-call turns persist as assistant rows with empty content
          // — those must NOT render as empty bubbles. role='tool' results
          // are dropped too (surfaced as build actions, not chat).
          .filter(
            (m) =>
              m.role === 'user' ||
              (m.role === 'assistant' && (m.content ?? '').trim().length > 0)
          )
          .map((m) => ({
            id: m.id,
            role: m.role as 'user' | 'assistant',
            content: m.content,
            createdAt: new Date(m.createdAt),
            quickReplies: Array.isArray(m.quickReplies) ? m.quickReplies : undefined,
          }))
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load conversation';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  // Send message (same as before)
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return;

      setError(null);

      const userMsg: Message = {
        id: `user-${Date.now()}`,
        role: 'user',
        content,
        createdAt: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      // Phase 2 observability: emit prompt-sent event BEFORE the
      // network call so we capture the intent even if the call fails.
      // Privacy floor: send message LENGTH only, never raw content.
      const correlationId = conversationIdRef.current ?? 'pending';
      aiObs.trackCopilotPromptSent(correlationId, {
        messageLength: content.length,
        hasTemplateContext: !!pageContext?.templateId,
      });
      const turnStartedAt = performance.now();

      try {
        const data = await callFirebaseFunction<AIChatResponse>('ai_chat', {
          organization_id: organizationId,
          message: content,
          conversation_id: conversationIdRef.current,
          page_context: pageContext ?? undefined,
        });

        conversationIdRef.current = data.conversation_id;
        // Track the (possibly just re-homed) scope so navigation to the built
        // template keeps THIS conversation instead of blanking it.
        activeTemplateIdRef.current = data.template_id ?? null;

        // A write happened this turn → suppress Build it/Adjust. The FE
        // only ever receives WRITE_TOOLS results in `actions`.
        if (data.actions && data.actions.length > 0) {
          setHasWriteActionOccurred(true);
        }

        // Phase 2 observability: emit response-received with the
        // server-enriched metrics (modelLatencyMs / toolCallCount /
        // tokens). Optional fields default to 0 when server omits them
        // (older deploys won't have them — be tolerant).
        const totalDurationMs = Math.round(performance.now() - turnStartedAt);
        const toolMetrics = data.toolMetrics ?? [];
        const inputTokens = data.usage?.inputTokens ?? 0;
        const outputTokens = data.usage?.outputTokens ?? 0;
        aiObs.trackCopilotResponseReceived(data.conversation_id, {
          modelLatencyMs: data.modelLatencyMs ?? 0,
          toolCallCount: toolMetrics.length,
          tokensIn: inputTokens,
          tokensOut: outputTokens,
          totalDurationMs,
          templateId: pageContext?.templateId,
        });

        // Per-tool events. Cap at 50 to defend against a runaway
        // agent loop spamming Mixpanel; if we hit this in prod the
        // cap itself is the signal.
        const TOOL_EMIT_CAP = 50;
        for (let i = 0; i < Math.min(toolMetrics.length, TOOL_EMIT_CAP); i++) {
          const m = toolMetrics[i];
          aiObs.trackCopilotToolCall(data.conversation_id, {
            toolName: m.toolName,
            success: m.success,
            durationMs: m.durationMs,
          });
          if (!m.success && m.errorClass) {
            aiObs.trackCopilotToolError(data.conversation_id, {
              toolName: m.toolName,
              errorClass: m.errorClass,
            });
          }
        }

        // Update session-lifetime counters used by SESSION_ENDED.
        sessionStatsRef.current.totalTurns += 1;
        sessionStatsRef.current.totalToolCalls += toolMetrics.length;
        sessionStatsRef.current.totalTokens += inputTokens + outputTokens;

        // Notify parent of tool actions (for navigation, animations, etc.)
        if (data.actions && data.actions.length > 0 && onActions) {
          onActions(data.actions);
        }

        const assistantMsg: Message = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: data.content,
          createdAt: new Date(),
          // Defensive: only trust a non-empty array of strings.
          quickReplies: Array.isArray(data.quickReplies)
            ? data.quickReplies.filter((q): q is string => typeof q === 'string' && q.length > 0)
            : undefined,
        };
        setMessages((prev) => [...prev, assistantMsg]);

        // Refresh conversation list after sending (updates titles/timestamps)
        loadConversationList();
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to get response';
        setError(errorMessage);

        const errorMsg: Message = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: `Sorry, I encountered an error: ${errorMessage}`,
          createdAt: new Date(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsLoading(false);
      }
    },
    [organizationId, isLoading, loadConversationList, aiObs, pageContext]
  );

  const clearChat = useCallback(() => {
    setMessages([]);
    conversationIdRef.current = null;
    setError(null);
    setHasWriteActionOccurred(false);
    // Reset session counters when conversation cleared — fresh
    // chat is conceptually a new session for analytics purposes.
    sessionStatsRef.current = { totalTurns: 0, totalToolCalls: 0, totalTokens: 0 };
  }, []);

  // Expose session stats + conversation id for the SESSION_ENDED
  // emit in the parent component (CopilotContext / drawer).
  const getSessionSnapshot = useCallback(
    (): { conversationId: string | null; totalTurns: number; totalToolCalls: number; totalTokens: number } => ({
      conversationId: conversationIdRef.current,
      totalTurns: sessionStatsRef.current.totalTurns,
      totalToolCalls: sessionStatsRef.current.totalToolCalls,
      totalTokens: sessionStatsRef.current.totalTokens,
    }),
    []
  );

  // Submit copilot feedback (per-message thumbs/comment OR general form).
  // Threads the live conversation id + template context internally so
  // callers only supply rating/comment. NEVER throws — returns
  // { ok: boolean } (mirrors the aiObservability safeEmit discipline) so
  // a failed feedback POST can't break the chat. Does NOT touch isLoading
  // (non-blocking).
  const submitFeedback = useCallback(
    async (args: {
      source: 'message' | 'general';
      rating?: 'up' | 'down';
      comment?: string;
      category?: string;
      messageId?: string;
      messageIndex?: number;
      messageText?: string;
    }): Promise<{ ok: boolean }> => {
      try {
        await callFirebaseFunction('ai_submitFeedback', {
          organization_id: organizationId,
          // conversation id is the best-available "trace" anchor today
          conversation_id: conversationIdRef.current ?? undefined,
          template_id: pageContext?.templateId ?? undefined,
          source: args.source,
          rating: args.rating ?? undefined,
          comment: args.comment ?? undefined,
          category: args.category ?? undefined,
          message_id: args.messageId ?? undefined,
          message_index:
            typeof args.messageIndex === 'number' ? args.messageIndex : undefined,
          // The assistant reply this feedback is on (per-message surface) so
          // the team can read WHAT was rated. Backend truncates.
          message_text: args.messageText ?? undefined,
        });
        return { ok: true };
      } catch (err) {
        console.debug('[copilot] submitFeedback failed', err);
        return { ok: false };
      }
    },
    [organizationId, pageContext]
  );

  // Track whether we've done the initial load once
  const hasInitialLoadRef = useRef(false);

  // Load the conversation list once on mount. WHICH conversation to show is
  // decided by the scope effect below (per-template isolation), not here.
  useEffect(() => {
    (async () => {
      if (hasInitialLoadRef.current) return;
      hasInitialLoadRef.current = true;

      setIsLoadingHistory(true);
      try {
        const data = await callFirebaseFunction<{ conversations: ConversationSummary[] }>(
          'ai_listConversations',
          { organization_id: organizationId }
        );
        setConversations(data.conversations);
      } catch (err) {
        console.error('Failed to load conversations:', err);
      } finally {
        setIsLoadingHistory(false);
        setIsListLoaded(true);
      }
    })();
  }, [organizationId]);

  // Auto-switch: keep the shown conversation aligned with the current page's
  // template scope (null = global). Because there is exactly one conversation
  // per template, this is what isolates context — no cross-template leaking.
  // Runs on navigation (pageContext.templateId change) and once the list loads.
  useEffect(() => {
    if (!isListLoaded) return;
    const scope = pageContext?.templateId ?? null;
    if (resolvedScopeRef.current === scope) return; // already resolved this scope

    // Keep the currently-shown conversation if it already matches the scope
    // (e.g. a just-re-homed build conversation) — no reliance on list freshness.
    if (conversationIdRef.current && activeTemplateIdRef.current === scope) {
      resolvedScopeRef.current = scope;
      return;
    }

    // Otherwise switch to the most-recent conversation for this scope, or blank.
    const match = conversations.find((c) => (c.templateId ?? null) === scope);
    resolvedScopeRef.current = scope;
    if (match) {
      if (match.id !== conversationIdRef.current) loadConversation(match.id);
    } else {
      // No conversation for this scope yet → blank; the first send creates one,
      // stamped with this scope by the backend.
      clearChat();
      activeTemplateIdRef.current = scope;
    }
  }, [isListLoaded, pageContext?.templateId, conversations, loadConversation, clearChat]);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    submitFeedback,
    clearChat,
    conversationId: conversationIdRef.current,
    /** True once a write action has happened in this conversation
     * (live build OR detected on reload). Gates the Build it/Adjust
     * chips so they vanish after the plan is implemented. */
    hasWriteActionOccurred,
    /** Session stats snapshot for analytics (Phase 2 observability).
     * Stable reference across renders — does not trigger re-renders
     * itself. Call before firing SESSION_ENDED. */
    getSessionSnapshot,
    // History
    conversations,
    isLoadingHistory,
    loadConversation,
    loadConversationList,
  };
}
